import React, { useState, useEffect } from 'react';
import axios from 'axios';

function Login({ onLogin, onBack }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [deviceId, setDeviceId] = useState('');

  // Generate or retrieve device ID
  useEffect(() => {
    let storedDeviceId = localStorage.getItem('deviceId');
    if (!storedDeviceId) {
      // Generate unique device ID based on browser fingerprint
      const fingerprint = [
        navigator.userAgent,
        navigator.language,
        window.screen.width + 'x' + window.screen.height,
        new Date().getTimezoneOffset(),
        Date.now(),
        Math.random()
      ].join('|');
      
      storedDeviceId = btoa(fingerprint).substring(0, 32);
      localStorage.setItem('deviceId', storedDeviceId);
    }
    setDeviceId(storedDeviceId);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/login', { 
        email, 
        password, 
        deviceId 
      });
      if (response.data.success) {
        // Store device ID for logout (keep consistent with validation)
        localStorage.setItem('deviceId', deviceId);
        if (response.data.token) localStorage.setItem('authToken', response.data.token);
        onLogin(response.data.user);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        setError(error.response.data.message || 'Maximum device limit reached');
      } else if (error.response?.status === 401) {
        setError('Invalid credentials');
      } else {
        setError('Login failed. Please try again.');
      }
    }
  };

  return (
    <div className="student-login-container">
      <div className="student-login-form">
        <div className="header">
          {onBack && (
            <button className="back-btn" onClick={onBack}>←</button>
          )}
          <div className="header-content">
            <div className="logo">
              <div className="logo-text">R</div>
            </div>
            <h1 className="school-title">The Rising School</h1>
            <p className="school-subtitle">Student & Staff Portal</p>
          </div>
        </div>

        <div className="form-container">
          <div className="welcome-text">
            <h2 className="welcome-title">Welcome Back!</h2>
            <p className="welcome-subtitle">Sign in to access school resources</p>
          </div>

          <form onSubmit={handleSubmit}>
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-group">
              <label className="form-label">Email:</label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Password:</label>
              <div className="password-container">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            
            <button type="submit" className="login-btn">
              Login to School Portal
            </button>
          </form>

          <div className="info-section">
            <h3 className="info-title">School Management System</h3>
            <div className="feature-item">
              <div className="feature-icon">📚</div>
              <span>Access assignments and study materials</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📊</div>
              <span>View attendance and grades</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🎮</div>
              <span>Educational games and quizzes</span>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📅</div>
              <span>School calendar and events</span>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .student-login-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          font-family: 'Inter', sans-serif;
        }

        .student-login-form {
          max-width: 400px;
          width: 100%;
          background: white;
          border-radius: 20px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .header {
          background: linear-gradient(135deg, #1E40AF, #3B82F6);
          padding: 30px;
          color: white;
          position: relative;
        }

        .back-btn {
          position: absolute;
          top: 20px;
          left: 20px;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          background: rgba(255,255,255,0.2);
          border: none;
          color: white;
          font-size: 18px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .header-content {
          text-align: center;
        }

        .logo {
          width: 60px;
          height: 60px;
          background: white;
          border-radius: 15px;
          margin: 0 auto 20px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-text {
          font-size: 24px;
          color: #1E40AF;
          font-weight: bold;
        }

        .school-title {
          font-size: 20px;
          font-weight: 600;
          margin-bottom: 5px;
        }

        .school-subtitle {
          font-size: 14px;
          opacity: 0.9;
        }

        .form-container {
          padding: 40px 30px;
        }

        .welcome-text {
          text-align: center;
          margin-bottom: 30px;
        }

        .welcome-title {
          font-size: 24px;
          font-weight: 600;
          color: #1F2937;
          margin-bottom: 8px;
        }

        .welcome-subtitle {
          font-size: 14px;
          color: #6B7280;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          margin-bottom: 8px;
        }

        .form-input {
          width: 100%;
          padding: 15px;
          border: 2px solid #E5E7EB;
          border-radius: 12px;
          font-size: 16px;
          background: #F9FAFB;
          transition: all 0.3s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: #1E40AF;
          background: white;
          box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
        }

        .password-container {
          position: relative;
        }

        .password-toggle {
          position: absolute;
          right: 15px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          font-size: 18px;
          color: #666;
        }

        .login-btn {
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, #1E40AF, #3B82F6);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-bottom: 30px;
        }

        .login-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(30, 64, 175, 0.3);
        }

        .error-message {
          color: #EF4444;
          background: #FEF2F2;
          border: 1px solid #FECACA;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 20px;
          font-size: 14px;
        }

        .info-section {
          background: #F8FAFC;
          padding: 20px;
          border-radius: 12px;
        }

        .info-title {
          font-size: 16px;
          font-weight: 600;
          color: #1F2937;
          margin-bottom: 15px;
          text-align: center;
        }

        .feature-item {
          display: flex;
          align-items: center;
          margin-bottom: 10px;
          font-size: 14px;
          color: #6B7280;
        }

        .feature-icon {
          width: 20px;
          height: 20px;
          margin-right: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
        }
      `}</style>
    </div>
  );
}

export default Login;